import { useEffect, useState } from 'react';
import { GitBranch, Lock } from 'lucide-react';
import { useAuth } from '../lib/auth-context';
import { divisionsAPI, VIEW_DIVISION_KEY } from '../lib/api';
import { clearAll } from '../lib/apiCache';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';

// Viewer-only: choose which division the read-only auditor is looking at.
export default function ViewerDivisionPicker() {
  const { user } = useAuth();
  const [divisions, setDivisions] = useState([]);
  const current = localStorage.getItem(VIEW_DIVISION_KEY) || '';
  const locked = !!user?.assigned_division_id;

  useEffect(() => {
    if (user?.role !== 'viewer') return;
    divisionsAPI.list().then(res => {
      const list = res.data || [];
      setDivisions(list);
      const valid = list.some(d => d._id === current);
      const target = locked ? user.assigned_division_id : (valid ? current : list[0]?._id);
      if (target && target !== current) {
        localStorage.setItem(VIEW_DIVISION_KEY, target);
        clearAll();
        window.location.reload();
      }
    }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.role]);

  if (user?.role !== 'viewer' || !divisions.length) return null;

  if (locked) {
    const name = divisions.find(d => d._id === user.assigned_division_id)?.name || 'Your division';
    return (
      <span className="inline-flex max-w-[140px] items-center gap-1.5 h-8 px-3 rounded-full border bg-muted/40 text-xs font-medium text-muted-foreground truncate" data-testid="viewer-division-locked">
        <Lock className="h-3 w-3" /> {name}
      </span>
    );
  }

  const change = (id) => {
    if (id === current) return;
    localStorage.setItem(VIEW_DIVISION_KEY, id);
    clearAll();
    window.location.reload();
  };

  return (
    <Select value={current} onValueChange={change}>
      <SelectTrigger className="h-8 w-[190px] rounded-full text-xs font-medium gap-1.5" data-testid="viewer-division-select">
        <GitBranch className="h-3.5 w-3.5 text-teal-600 shrink-0" />
        <SelectValue placeholder="Pick a division" />
      </SelectTrigger>
      <SelectContent>
        {divisions.map(d => (
          <SelectItem key={d._id} value={d._id} data-testid={`viewer-division-option-${d._id}`}>{d.name}</SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}
